import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CaptchaComponent } from '@freelancer/captcha';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import { ResponseData } from '@freelancer/freelancer-http';
import type { LoginError } from '@freelancer/login-signup';
import { LogoSize } from '@freelancer/ui/logo';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import type { FormGroupTypeOf } from '@freelancer/utils';
import { ErrorCodeApi } from 'api-typings/errors/errors';

@Component({
  standalone: false,
  selector: 'app-captcha-form',
  template: `
    <app-form-logo
      [showBackButton]="true"
      [size]="logoSize"
      [backTrackingLabel]="'CaptchaForm-Back'"
      (back)="back.emit()"
    ></app-form-logo>
    <form
      class="Form"
      [formGroup]="formGroup"
      (ngSubmit)="handleComplete()"
    >
      <h1
        class="text-mid Form-heading mb-xxsmall"
        i18n="Signup captcha heading"
      >
        You're almost there!
      </h1>
      <div class="Form-captchaSection mb-small">
        <fl-captcha
          #captchaElement
          flTrackingLabel="CaptchaForm-CaptchaElement"
          [control]="formGroup.controls.response"
        ></fl-captcha>
      </div>
      @if (
        response &&
        response.status === 'error' &&
        response.errorCode !== ErrorCodeApi.AUTH_CAPTCHA_REQUIRED
      ) {
        <app-login-signup-errors
          class="mb-small"
          [response]="response"
          (switchToLogin)="switchToLogin.emit()"
        ></app-login-signup-errors>
      }
      <app-login-signup-button
        i18n="Signup captcha signup button"
        [busy]="busy"
        [stepLabel]="'CaptchaForm'"
      >
        Join {{ partnerDomain }}
      </app-login-signup-button>
    </form>
  `,
  styleUrls: ['./captcha-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaptchaFormComponent {
  ErrorCodeApi = ErrorCodeApi;

  @Input() logoSize = LogoSize.MID;
  @Input() formGroup: FormGroup<
    FormGroupTypeOf<{ challenge: string; response: string }>
  >;
  @Input() response?: ResponseData<unknown, BackendAllErrorCodes | LoginError>;
  @Input() partnerDomain?: string;
  @Input() busy = false;

  @Output() back = new EventEmitter();
  @Output() complete = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();

  @ViewChild('captchaElement') captchaElement: CaptchaComponent;

  handleComplete(): void {
    if (this.response === null) {
      return;
    }
    dirtyAndValidate(this.formGroup);
    if (this.formGroup.valid) {
      this.complete.emit();
    }
  }
}
